import { handlePostApi } from '@/api/crud';
import {
  branchApiEndpoints,
  companyAccessEndpoints,
  menuApiEndpoints,
  roleApiEndpoints,
} from '@/api/endpoints';
import { useMutateGetApi } from '@/apiError/apiShow';
import CustomTree from '@/components/admin/tree/Tree';
import { useFetch } from '@/hooks';
import { optionSearchFunction } from '@/utils/common';
import GlobalDivider from '@/utils/divider/GlobalDivider';
import { rules } from '@/utils/validation/FormValidation';
import { SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Layout,
  Row,
  Select,
  Space,
} from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllKeys } from './role-menu-mapping';

const { Content } = Layout;

const PermissionMenuMapping = () => {
  const [permissions, setPermissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState([]);
  const [roleList, setRoleList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [selectRoles, setSelectRoles] = useState();
  const [loading, setLoading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [checkAll, setCheckAll] = useState(false);
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [filteredPermissions, setFilteredPermissions] = useState([]);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const { data: roleData } = useFetch(roleApiEndpoints.retrieveList);
  const { data: companyList } = useFetch(companyAccessEndpoints.retrieveList);

  const { mutateAsync: getRole, isPending: roleLoading } = useMutateGetApi();

  const onExpand = (nodeKey, expanded) => {
    if (expanded) {
      setExpandedKeys((prev) => [...prev, nodeKey]);
    } else {
      setExpandedKeys((prev) => prev.filter((key) => key !== nodeKey));
    }
    setAutoExpandParent(false);
  };

  const onCheck = (nodeKey, checked) => {
    let newCheckedKeys;
    if (checked) {
      newCheckedKeys = [...checkedKeys, nodeKey];
    } else {
      newCheckedKeys = checkedKeys.filter((key) => key !== nodeKey);
    }

    setCheckedKeys(newCheckedKeys);

    // Update permissions based on tree selection
    setPermissions((prevPermissions) => {
      const updatePermissionsByChecked = (items) => {
        return items.map((item) => {
          if (item.key === nodeKey) {
            return {
              ...item,
              create: checked,
              view: checked,
              edit: checked,
              delete: checked,
              full: checked,
            };
          }

          if (item.children && item.children.length > 0) {
            return {
              ...item,
              children: updatePermissionsByChecked(item.children),
            };
          }

          return item;
        });
      };

      const updated = updatePermissionsByChecked(prevPermissions);
      setFilteredPermissions(
        searchTerm ? filterPermissionsBySearch(updated, searchTerm) : updated,
      );
      return updated;
    });

    // Update checkAll state
    const allKeys = getAllKeys(filteredPermissions);
    setCheckAll(newCheckedKeys.length === allKeys.length && allKeys.length > 0);
  };

  const onSelectAll = () => {
    const newCheckAll = !checkAll;
    setCheckAll(newCheckAll);

    // Update all permissions and checked keys
    setPermissions((prevPermissions) => {
      const updateAllPermissions = (items) => {
        return items.map((item) => ({
          ...item,
          create: newCheckAll,
          view: newCheckAll,
          edit: newCheckAll,
          delete: newCheckAll,
          full: newCheckAll,
          children: item.children ? updateAllPermissions(item.children) : [],
        }));
      };

      const updated = updateAllPermissions(prevPermissions);
      setFilteredPermissions(
        searchTerm ? filterPermissionsBySearch(updated, searchTerm) : updated,
      );
      return updated;
    });

    if (newCheckAll) {
      const allKeys = getAllKeys(
        searchTerm ? filteredPermissions : permissions,
      );
      setCheckedKeys(allKeys);
    } else {
      setCheckedKeys([]);
    }
  };

  const onPermissionCheck = (nodeKey, permissionType, checked) => {
    setPermissions((prevPermissions) => {
      const updatePermissions = (items) => {
        return items.map((item) => {
          if (item.key === nodeKey) {
            const updatedItem = { ...item, [permissionType]: checked };

            // If checking "Full", set all other permissions to true
            if (permissionType === 'full' && checked) {
              updatedItem.create = true;
              updatedItem.view = true;
              updatedItem.edit = true;
              updatedItem.delete = true;
            }

            // If unchecking "Full", set all other permissions to false
            if (permissionType === 'full' && !checked) {
              updatedItem.create = false;
              updatedItem.view = false;
              updatedItem.edit = false;
              updatedItem.delete = false;
            }

            // If unchecking any specific permission, uncheck "Full"
            if (
              ['create', 'view', 'edit', 'delete'].includes(permissionType) &&
              !checked
            ) {
              updatedItem.full = false;
            }

            // If all specific permissions are checked, check "Full"
            if (
              ['create', 'view', 'edit', 'delete'].includes(permissionType) &&
              checked
            ) {
              if (
                updatedItem.create &&
                updatedItem.view &&
                updatedItem.edit &&
                updatedItem.delete
              ) {
                updatedItem.full = true;
              }
            }

            // Update tree checked state based on permissions
            const hasAnyPermission =
              updatedItem.create ||
              updatedItem.view ||
              updatedItem.edit ||
              updatedItem.delete ||
              updatedItem.full;

            if (hasAnyPermission && !checkedKeys.includes(nodeKey)) {
              setCheckedKeys((prev) => [...prev, nodeKey]);
            } else if (!hasAnyPermission && checkedKeys.includes(nodeKey)) {
              setCheckedKeys((prev) => prev.filter((key) => key !== nodeKey));
            }

            return updatedItem;
          }

          if (item.children && item.children.length > 0) {
            return { ...item, children: updatePermissions(item.children) };
          }

          return item;
        });
      };

      const updated = updatePermissions(prevPermissions);
      setFilteredPermissions(
        searchTerm ? filterPermissionsBySearch(updated, searchTerm) : updated,
      );

      // Update checkAll state based on current permissions
      const dataToCheck = searchTerm
        ? filterPermissionsBySearch(updated, searchTerm)
        : updated;
      const allKeys = getAllKeys(dataToCheck);
      const hasAllPermissions = allKeys.every((key) => {
        const findItem = (items, targetKey) => {
          for (const item of items) {
            if (item.key === targetKey) return item;
            if (item.children) {
              const found = findItem(item.children, targetKey);
              if (found) return found;
            }
          }
          return null;
        };

        const item = findItem(updated, key);
        return (
          item &&
          (item.create || item.view || item.edit || item.delete || item.full)
        );
      });

      setCheckAll(hasAllPermissions && allKeys.length > 0);

      return updated;
    });
  };

  const filterPermissionsBySearch = (data, searchValue) => {
    return data.reduce((acc, item) => {
      const matchesSearch = item.title
        .toLowerCase()
        .includes(searchValue.toLowerCase());
      const filteredChildren = item.children
        ? filterPermissionsBySearch(item.children, searchValue)
        : [];

      if (matchesSearch || filteredChildren.length > 0) {
        acc.push({
          ...item,
          children: filteredChildren,
        });
      }
      return acc;
    }, []);
  };

  const generateSubMenuPermissions = (subMenus) => {
    return subMenus.map((subMenu) => ({
      key: subMenu.referenceId,
      title: subMenu.name,
      create: false,
      view: false,
      edit: false,
      delete: false,
      full: false,
      children: subMenu.subMenus
        ? generateSubMenuPermissions(subMenu.subMenus)
        : [],
    }));
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (!value.trim()) {
      setFilteredPermissions(permissions);
      // Update checkAll state for full list
      const allKeys = getAllKeys(permissions);
      const hasAllPermissions = allKeys.every((key) => {
        const findItem = (items, targetKey) => {
          for (const item of items) {
            if (item.key === targetKey) return item;
            if (item.children) {
              const found = findItem(item.children, targetKey);
              if (found) return found;
            }
          }
          return null;
        };

        const item = findItem(permissions, key);
        return (
          item &&
          (item.create || item.view || item.edit || item.delete || item.full)
        );
      });
      setCheckAll(hasAllPermissions && allKeys.length > 0);
      return;
    }

    const filtered = filterPermissionsBySearch(permissions, value);
    setFilteredPermissions(filtered);

    // Update checkAll state for filtered list
    const allKeys = getAllKeys(filtered);
    const hasAllPermissions = allKeys.every((key) => {
      const findItem = (items, targetKey) => {
        for (const item of items) {
          if (item.key === targetKey) return item;
          if (item.children) {
            const found = findItem(item.children, targetKey);
            if (found) return found;
          }
        }
        return null;
      };

      const item = findItem(permissions, key);
      return (
        item &&
        (item.create || item.view || item.edit || item.delete || item.full)
      );
    });
    setCheckAll(hasAllPermissions && allKeys.length > 0);
  };

  const buildMenuDetails = () => {
    const menuDetails = [];

    const processNode = (node) => {
      // Only include nodes that have at least one permission checked
      if (node.create || node.view || node.edit || node.delete || node.full) {
        menuDetails.push({
          menuId: node.key,
          title: node.title,
          isCreate: node.create,
          isDelete: node.delete,
          isEdit: node.edit,
          isView: node.view,
          isFull: node.full,
        });
      }

      if (node.children) {
        node.children.forEach(processNode);
      }
    };

    permissions.forEach(processNode);
    return menuDetails;
  };

  const onFinish = async () => {
    const menuDetails = buildMenuDetails();

    await handlePostApi(
      roleApiEndpoints.menuMapping,
      {
        branchId: selectedBranch,
        companyId: selectedCompany,
        roleId: selectRoles,
        menuDetails,
      },
      setLoading,
      form,
    );

    // Reset form and state
    form.resetFields();
    setSelectedCompany([]);
    setSelectRoles();
    setSearchTerm('');
    setCheckedKeys([]);
    setCheckAll(false);

    // Reset permissions
    const resetPermissions = (items) => {
      return items.map((item) => ({
        ...item,
        create: false,
        view: false,
        edit: false,
        delete: false,
        full: false,
        children: item.children ? resetPermissions(item.children) : [],
      }));
    };

    setPermissions((prev) => resetPermissions(prev));
    setFilteredPermissions((prev) => resetPermissions(prev));
    navigate(-1);
  };

  const fetchMenuList = async (branchId) => {
    try {
      const { data } = await menuApiEndpoints.retrieveListWithVersion({
        companyId: selectedCompany,
        branchId: branchId,
      });
      const initialPermissions = data.data.menus?.map((menu) => ({
        key: menu.referenceId,
        title: menu.name,
        create: false,
        view: false,
        edit: false,
        delete: false,
        full: false,
        children: menu.subMenus
          ? generateSubMenuPermissions(menu.subMenus)
          : [],
      }));
      setPermissions(initialPermissions || []);
      setFilteredPermissions(initialPermissions || []);
    } catch (error) {
      console.error('Error fetching menu list:', error);
    }
  };

  const handleCompanyChange = async (value) => {
    setSelectedCompany(value);
    form.setFieldsValue({ branchId: null });
    form.setFieldsValue({ roleId: null });
    const { data } = await branchApiEndpoints.companyWiseBranch(value);
    setBranchList(data?.data);
    setPermissions([]);
    setFilteredPermissions([]);
  };

  const handleBranchChange = async (value) => {
    setPermissions([]);
    setFilteredPermissions([]);
    setSelectedBranch(value);
    form.setFieldsValue({ roleId: null });
    const res = await getRole({
      url: `roles/by-company-branch/${selectedCompany}/${value}/`,
    });
    setRoleList(res?.data);
    fetchMenuList(value);
  };

  const handleRoleChange = (value) => {
    setSelectRoles(value);
  };

  return (
    <>
      <Layout style={{ minHeight: '100vh' }}>
        <Layout>
          <Content style={{ padding: '24px' }}>
            <GlobalDivider name="Role Menu Mapping" />
            <Form form={form} onFinish={onFinish}>
              <Row gutter={16} style={{ marginBottom: '16px' }}>
                <Col span={12}>
                  <Form.Item
                    name="companyId"
                    validateTrigger="onChange"
                    rules={rules.required}
                  >
                    <Select
                      showSearch
                      placeholder="Select Company"
                      filterOption={optionSearchFunction}
                      options={companyList?.map((item) => ({
                        value: item.referenceId,
                        label: item?.name,
                      }))}
                      onChange={handleCompanyChange}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="branchId"
                    validateTrigger="onChange"
                    rules={rules.required}
                  >
                    <Select
                      showSearch
                      disabled={!branchList?.length}
                      placeholder="Select Branch"
                      filterOption={optionSearchFunction}
                      options={branchList?.map((item) => ({
                        value: item.referenceId,
                        label: item?.branchName,
                      }))}
                      onChange={handleBranchChange}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="roleId"
                    validateTrigger="onChange"
                    rules={rules.required}
                  >
                    <Select
                      showSearch
                      placeholder="Select Role"
                      loading={roleLoading}
                      filterOption={optionSearchFunction}
                      options={roleList?.map((item) => ({
                        value: item.referenceId,
                        label: item?.name,
                      }))}
                      onChange={handleRoleChange}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* <Row gutter={16} style={{ marginBottom: '16px' }}> */}
              <Input
                placeholder="Search menu..."
                className="tw-mb-4"
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {/* </Row> */}

              <Checkbox onClick={onSelectAll} className="tw-w-full tw-mb-4">
                Select All
              </Checkbox>

              <CustomTree
                treeData={filteredPermissions}
                onPermissionChange={onPermissionCheck}
                checkedKeys={checkedKeys}
                onCheck={onCheck}
              />

              <div style={{ textAlign: 'right', marginTop: '12px' }}>
                <Space size="large">
                  <Button
                    htmlType="submit"
                    className="submit-button"
                    loading={loading}
                    type="primary"
                  >
                    Create
                  </Button>
                  <Button
                    className="cancel-button"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                </Space>
              </div>
            </Form>
          </Content>
        </Layout>
      </Layout>
    </>
  );
};

export default PermissionMenuMapping;
